import { Fragment } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { BlockWrapper } from './blocks/BlockWrapper'
import type { PageBlock, DevicePreview } from '../../types'
import { DEVICE_WIDTHS } from '../../types'

interface BuilderCanvasProps {
  blocks: PageBlock[]
  selectedBlockId: string | null
  devicePreview: DevicePreview
  showPreview: boolean
  isPaletteDragging: boolean
  onSelectBlock: (id: string) => void
  onDeleteBlock: (id: string) => void
  onDuplicateBlock: (id: string) => void
}

/** Drop indicator line shown between blocks when dragging from palette */
function GapIndicator({ id }: { id: string }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div ref={setNodeRef} className="py-2.5">
      <div
        className={`h-1.5 rounded-full transition-all duration-150 ${ isOver ? 'bg-primary shadow-[0_0_8px_rgba(59,130,246,0.4)]' : 'bg-primary/20 dark:bg-primary/30' }`}
      />
    </div>
  )
}

function CanvasDropZone({ children, devicePreview }: { children: React.ReactNode; devicePreview: DevicePreview }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas-drop-zone' })

  return (
    <div
      ref={setNodeRef}
      className={`${DEVICE_WIDTHS[devicePreview]} mx-auto min-h-[200px] transition-colors rounded-lg ${isOver ? 'bg-primary/10 ring-2 ring-primary/50 ring-dashed' : ''}`}
    >
      {children}
    </div>
  )
}

export function BuilderCanvas({
  blocks,
  selectedBlockId,
  devicePreview,
  showPreview,
  isPaletteDragging,
  onSelectBlock,
  onDeleteBlock,
  onDuplicateBlock,
}: BuilderCanvasProps) {
  if (showPreview) {
    return null
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-background">
      <CanvasDropZone devicePreview={devicePreview}>
        {blocks.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-default-300 rounded-lg bg-content1">
            <p className="text-default-500 text-sm">Drag a block from the left panel or click to add</p>
          </div>
        ) : (
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            {isPaletteDragging && <GapIndicator id="gap-0" />}
            {blocks.map((block, index) => (
              <Fragment key={block.id}>
                <div className={index > 0 && !isPaletteDragging ? 'mt-3' : ''}>
                  <BlockWrapper
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onSelect={() => onSelectBlock(block.id)}
                    onDelete={() => onDeleteBlock(block.id)}
                    onDuplicate={() => onDuplicateBlock(block.id)}
                  />
                </div>
                {isPaletteDragging && <GapIndicator id={`gap-${index + 1}`} />}
              </Fragment>
            ))}
          </SortableContext>
        )}
      </CanvasDropZone>
    </div>
  )
}
